import React, { useContext, useEffect, useRef } from 'react';
import { debounce } from 'lodash-es';
import { SurveyDefinition, WebViewStepDefinition } from '../../../types/Survey';
import { StepResultModel, SurveyResultModel } from '../../../types/SurveyResult';
import { TranslateContext } from '../../../widgets/MyDataHelps/TranslateContext';
import { SurveyStepContext } from '../Util/surveyStepContext';
import { DelegatedOAuthClient } from '../../../context/PageSetupContext';
import { buildStepResult } from '../Util/surveyStepUtils';
import {
    acceptableEventSource,
    assembleWebViewContent,
    buildCurrentSurveyAnswers,
    buildFrameUrl,
    buildSandboxAttributes,
    buildStepConfiguration,
    buildSurveyContext,
} from '../Util/webViewStepUtils';
import { rendersDark } from '../../../helpers/surveyColorScheme';
import appActionHandler from '../../../helpers/appActionHandler';
import { connectExternalAccount, promptForAccountRemoval } from '../../../helpers/providerAccounts';
import { getDeviceInfo } from '../../../helpers/MyDataHelps/participantDeviceInfo';
import { getDelegatedAccessToken } from '../../../api/delegatedAccessToken';
import { makeUrl } from '../../../api/makeUrl';
import webViewContentHtml from './WebViewContent.html';
import webViewStructuredHtml from './WebViewStructuredHtml.html';

export interface WebViewStepProps {
    step: WebViewStepDefinition;
    surveyDefinition: SurveyDefinition;
    presentedDate?: string;
    participantID?: string;
    getSurveyResult: () => SurveyResultModel;
    onContinue: (stepResult: StepResultModel) => void;
}

export default function WebViewStep(props: WebViewStepProps) {
    const { step, surveyDefinition, presentedDate, participantID, getSurveyResult, onContinue } = props;

    const translate = useContext(TranslateContext);
    const surveyStepContext = useContext(SurveyStepContext);
    const mode = surveyStepContext?.mode ?? 'Survey';
    const webViewStepFrameUrl = surveyStepContext?.webViewStepFrameUrl ?? '';
    const delegatedOAuthClient = surveyStepContext?.delegatedOAuthClient ?? ({} as DelegatedOAuthClient);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const frameUrlRef = useRef<string | undefined>(undefined);
    const stepCompletedRef = useRef<boolean>(false);

    // Latest props/context, read by the (stable) message handlers without re-registering listeners.
    const latest = useRef({
        step,
        surveyDefinition,
        presentedDate,
        participantID,
        getSurveyResult,
        onContinue,
        translate,
        mode,
        delegatedOAuthClient,
        webViewStepFrameUrl,
    });
    latest.current = {
        step,
        surveyDefinition,
        presentedDate,
        participantID,
        getSurveyResult,
        onContinue,
        translate,
        mode,
        delegatedOAuthClient,
        webViewStepFrameUrl,
    };

    function postToFrame(message: any) {
        const frame = iframeRef.current;
        const frameUrl = frameUrlRef.current;
        if (frame?.contentWindow && frameUrl) {
            frame.contentWindow.postMessage(message, frameUrl);
        }
    }

    function resetFrameHeight() {
        const container = containerRef.current;
        const iframe = iframeRef.current;
        if (!container || !iframe) return;
        const surveyContent = container.closest('.current-survey-content') as HTMLElement | null;
        if (!surveyContent) return;
        iframe.style.height = surveyContent.clientHeight - iframe.offsetTop + 'px';
    }

    function renderedColorScheme(): string {
        return containerRef.current && rendersDark(containerRef.current) ? 'dark' : 'light';
    }

    function reloadFrame() {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const ctx = latest.current;

        resetFrameHeight();

        const colorScheme = renderedColorScheme();
        const newFrameUrl = buildFrameUrl(ctx.step.url, ctx.webViewStepFrameUrl, ctx.translate?.use(), colorScheme);
        if (newFrameUrl) {
            frameUrlRef.current = newFrameUrl;
        }
        const frameUrl = frameUrlRef.current;

        const baseUrl = window.location.protocol + '//' + window.location.host + makeUrl('/');
        const content = assembleWebViewContent({
            step: ctx.step,
            surveyDefinition: ctx.surveyDefinition,
            surveyResult: ctx.getSurveyResult(),
            baseUrl,
            webViewContentHtml,
            webViewStructuredHtml,
            colorScheme,
        });

        iframe.onload = () => {
            if (frameUrl) {
                iframe.contentWindow?.postMessage({ name: 'RKStudioStepContent', content }, frameUrl);
            }
        };
        iframe.src = frameUrl ?? '';

        stepCompletedRef.current = false;
    }

    function webViewMessageHandler(event: MessageEvent) {
        const ctx = latest.current;
        if (!acceptableEventSource(event, frameUrlRef.current, iframeRef.current?.contentWindow)) {
            return;
        }

        const data = event.data;
        if (data && (data.name === 'RKStudioWebViewStepAnswer' || data.name === 'CompleteStep')) {
            if (!stepCompletedRef.current) {
                stepCompletedRef.current = true;
                const startDate = ctx.presentedDate ?? new Date().toISOString();
                ctx.onContinue(
                    buildStepResult(ctx.step.identifier ?? '', startDate, [
                        {
                            Identifier: ctx.step.identifier ?? '',
                            Type: 'WebViewStepResult',
                            StartDate: startDate,
                            Result: data.answer,
                        } as any,
                    ]),
                );
            }
        } else if (data && (data.name === 'GetAccessToken' || data.name === 'GetDelegatedAccessToken')) {
            if (ctx.participantID) {
                if (ctx.delegatedOAuthClient.standaloneMode) {
                    return;
                }
                getDelegatedAccessToken({
                    participantID: ctx.participantID,
                    clientID: ctx.delegatedOAuthClient.ClientID ?? '',
                    clientSecret: ctx.delegatedOAuthClient.ClientSecret ?? '',
                    accessToken: appActionHandler.isEmbedded ? appActionHandler.token?.access_token : undefined,
                })
                    .then(response => {
                        postToFrame({
                            action: data.name,
                            success: true,
                            data: response,
                            messageID: data.messageID,
                            baseUrl: window.location.origin + makeUrl('/'),
                        });
                    })
                    .catch(() => {
                        postToFrame({ action: data.name, success: false, message: 'Unable to retrieve access token', messageID: data.messageID });
                    });
            } else {
                postToFrame({ action: data.name, success: false, message: 'Access token unavailable in this survey mode', messageID: data.messageID });
            }
        } else if (data && data.name === 'DeleteProviderAccount') {
            const contentWindow = iframeRef.current?.contentWindow;
            const frameUrl = frameUrlRef.current;
            if (contentWindow && frameUrl) {
                promptForAccountRemoval(
                    data,
                    contentWindow,
                    frameUrl,
                    ctx.translate?.instant('delete-account-confirm-message', null, null, 'Do you really want to delete this account?'),
                );
            }
        } else if (data && data.name === 'ConnectExternalAccount') {
            if (!appActionHandler.isEmbedded) {
                connectExternalAccount(data.externalAccountProviderID, data.messageID, data.openNewWindow, data.width, data.height);
            } else {
                appActionHandler
                    .connectExternalAccount(data.externalAccountProviderID, { openNewWindow: data.openNewWindow, width: data.width, height: data.height })
                    .then(() => {
                        postToFrame({ action: data.name, success: true, messageID: data.messageID });
                    })
                    .catch((error: any) => {
                        postToFrame({ action: data.name, success: false, errorMessage: error, messageID: data.messageID });
                    });
            }
        } else if (data && data.name === 'GetDeviceInfo') {
            if (!appActionHandler.isEmbedded) {
                const deviceInfo = getDeviceInfo();
                postToFrame({ action: data.name, success: true, data: deviceInfo, messageID: data.messageID });
            } else {
                appActionHandler.getDeviceInfo().then(deviceInfo => {
                    postToFrame({ action: data.name, success: true, data: deviceInfo, messageID: data.messageID });
                });
            }
        } else if (data && data.name === 'ShowParticipantWebVisualizationPDF') {
            if (!appActionHandler.isEmbedded) {
                let url =
                    'webvisualization/webvisualizationpdf?visualizationKey=' +
                    encodeURIComponent(data.visualizationKey) +
                    '&patientID=' +
                    encodeURIComponent(ctx.participantID ?? '') +
                    '&restrictToProject=true';
                if (data.landscape) {
                    url += '&landscape=true';
                }
                if (data.htmlViewerZoom) {
                    url += '&htmlViewerZoom=' + encodeURIComponent(data.htmlViewerZoom);
                }
                window.open(makeUrl(url));
            } else {
                appActionHandler.showParticipantWebVisualizationPDF(data);
            }
        } else if (data && data.name === 'GetStepConfiguration') {
            const stepConfig = buildStepConfiguration(ctx.surveyDefinition, ctx.step);
            postToFrame({ action: data.name, success: true, data: stepConfig, messageID: data.messageID });
        } else if (data && data.name === 'GetSurveyContext') {
            const surveyContext = buildSurveyContext(ctx.mode);
            postToFrame({ action: data.name, success: true, data: surveyContext, messageID: data.messageID });
        } else if (data && data.name === 'GetCurrentSurveyAnswers') {
            const surveyAnswers = buildCurrentSurveyAnswers(ctx.getSurveyResult(), ctx.surveyDefinition.steps);
            postToFrame({ action: data.name, success: true, data: surveyAnswers, messageID: data.messageID });
        } else if (data && data.name === 'RequestSensorKitAuthorization') {
            appActionHandler.requestSensorKitAuthorization(data);
        } else if (data && data.name === 'OpenEmbeddedUrl') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.openEmbeddedUrl(data.url);
            } else {
                window.open(data.url, '_blank');
            }
        } else if (data && data.name === 'OpenExternalUrl') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.openExternalUrl(data.url);
            } else {
                window.open(data.url, '_blank');
            }
        } else if (data && data.name === 'HealthConnectStatus') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.getHealthConnectStatus().then(status => {
                    postToFrame({ action: data.name, success: true, data: status, messageID: data.messageID });
                });
            }
        } else if (data && data.name === 'HealthConnectPrompt') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.showHealthConnectPrompt();
            }
        } else if (data && data.name === 'HealthConnectPhrStatus') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.getHealthConnectPhrStatus().then(status => {
                    postToFrame({ action: data.name, success: true, data: status, messageID: data.messageID });
                });
            }
        } else if (data && data.name === 'HealthConnectPhrPrompt') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.showHealthConnectPrhPrompt();
            }
        } else if (data && data.name === 'HealthConnectSettings') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.showHealthConnectSettings();
            }
        } else if (data && data.name === 'HealthConnectMatchmaking') {
            if (appActionHandler.isEmbedded) {
                appActionHandler.showHealthConnectMatchmakingPrompt();
            }
        }
    }

    useEffect(() => {
        let active = true;

        const messageListener = (event: MessageEvent) => webViewMessageHandler(event);
        window.addEventListener('message', messageListener);

        // External-account connect completes out of band: forward the raw
        // ConnectExternalAccountResponse window message and the ConnectExternalAccount
        // BroadcastChannel message into the frame.
        const forwardExternalAccountResponse = (responseData: any) => {
            if (responseData && responseData.name === 'ConnectExternalAccountResponse') {
                postToFrame({
                    action: 'connectExternalAccountDidFinish',
                    messageID: responseData.messageID,
                    success: responseData.success,
                    errorMessage: responseData.errorMessage,
                });
            }
        };
        const externalAccountMessageListener = (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            forwardExternalAccountResponse(event.data);
        };
        window.addEventListener('message', externalAccountMessageListener);
        const broadcastChannel = new BroadcastChannel('ConnectExternalAccount');
        broadcastChannel.onmessage = event => forwardExternalAccountResponse(event.data);

        const resizeListener = () => resetFrameHeight();
        if (latest.current.mode !== 'Design') {
            window.addEventListener('resize', resizeListener);
        }

        // HACK - See AppActionHandler setActionResult. No unsubscribe is available, so guard
        // with `active` so a stale callback after unmount does not post to a detached iframe.
        appActionHandler?.onMessageReceived(message => {
            if (active) {
                postToFrame(message);
            }
        });

        return () => {
            active = false;
            window.removeEventListener('message', messageListener);
            window.removeEventListener('message', externalAccountMessageListener);
            window.removeEventListener('resize', resizeListener);
            broadcastChannel.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Debounce reloads 50ms so rapid successive prop changes coalesce into one frame reload.
    const reloadFrameDebounced = useRef(debounce(() => reloadFrame(), 50));
    useEffect(() => {
        const debounced = reloadFrameDebounced.current;
        debounced();
        return () => debounced.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step.html, JSON.stringify(step.fieldValues), step.identifier]);

    useEffect(() => {
        if (mode !== 'Design') {
            const id = window.setTimeout(() => resetFrameHeight(), 0);
            return () => window.clearTimeout(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, step.identifier]);

    useEffect(() => {
        const container = containerRef.current;
        if (mode === 'Survey' || !container) return;
        const schemeRoot = container.closest('.survey-scheme-dark, .survey-scheme-light') ?? document.body;
        let last = rendersDark(container);
        const observer = new MutationObserver(() => {
            const next = rendersDark(container);
            if (next !== last) {
                last = next;
                reloadFrameDebounced.current();
            }
        });
        observer.observe(schemeRoot, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, step.identifier]);

    return (
        <div ref={containerRef}>
            <div className="current-step web-view-step">
                <iframe
                    ref={iframeRef}
                    data-testid="web-view-step-frame"
                    allowFullScreen
                    src=""
                    tabIndex={0}
                    style={{ border: 'none', padding: 0, margin: 0, width: '100%', display: 'block' }}
                    sandbox={buildSandboxAttributes(appActionHandler.isEmbedded)}
                    allow="camera; microphone; clipboard-write; geolocation"
                />
            </div>
        </div>
    );
}
